import { observer } from 'mobx-react-lite';
import { lazy, useEffect, type ComponentProps } from 'react';
import { Navigate, createBrowserRouter, type RouteObject, RouterProvider } from 'react-router';

import { PATHS, PATH_NAMES, type NavinaPath } from './AppDomains';
import { LogoDestination } from './assets/Logo/Logo';
import { getOrCreatePatientPortraitLifecycleModel } from './components/PatientPortraitLifecycle/PatientPortraitLifecycleModel';
import { tracePatientPortraitsOnRouter } from './components/PatientPortraitLifecycle/tracePatientPortraitsOnRouter';
import { FAVICON_COLOR_OVERRIDE } from './config/Environment';
import { ErrorBoundary } from './ErrorBoundary';
import { setFaviconColor } from './favicon';
import { NavinaLoginStep } from './pages/auth/steps/NavinaLoginStepEnum';
import { useFeatureFlag } from './pages/summary/behaviours/useFeatureFlag';
import { SummaryLandingPage } from './pages/summary/SummaryLandingPage.loader';
import { GuestOnlyRoute } from './react-router/auth/GuestOnlyRoute';
import { ProtectedRoute } from './react-router/auth/ProtectedRoute';
import { AdminLayout } from './react-router/layouts/AdminLayout/AdminLayout';
import { AuthLayout } from './react-router/layouts/AuthLayout';
import { MainLayout, type LayoutHeaderProps } from './react-router/layouts/MainLayout/MainLayout';
import { ApplicationsMenuSelectedPage } from './react-router/layouts/MainLayout/MainLayoutHeader';
import { ResponsivePaddingLayoutContainer } from './react-router/layouts/ResponsivePaddingLayout';
import { RootLayout } from './react-router/layouts/RootLayout';
import { SpinnerLayout } from './react-router/layouts/SpinnerLayout';
import { withLayout } from './react-router/layouts/withLayout';
import { PATIENT_PORTRAIT_ROUTES } from './react-router/routes/PatientPortraitRoutes/PatientPortraitRoutes';
import {
	EMBEDDED_LEGACY_APPOINTMENT_PORTRAIT_PATH_PREFIX,
	EMBEDDED_PATIENT_PORTRAIT_PATH_PREFIX,
} from './react-router/routes/PatientPortraitRoutes/PatientPortraitRoutes.utils';
import { POPULATION_HEALTH_MANAGEMENT_ROUTES } from './react-router/routes/PopulationHealthManagementRoutes/PopulationHealthManagementRoutes';
import { LAUNCH_DARKLY_PAGE_NAMES, withLaunchDarklyPage } from './react-router/withLaunchDarkly';
import 'normalize.css';
import '@navina/design-system/index.css';

import { slownessMeasure } from './utilities/performances/SlownessTask';

const {
	appointmentBasedSummary: APPOINTMENT_BASED_SUMMARY_PAGE_NAME,
	schedulingOverview: SCHEDULING_OVERVIEW_PAGE_NAME,
	patientLookup: PATIENT_LOOKUP_PAGE_NAME,
	analyticsDashboard: ANALYTICS_DASHBOARD_PAGE_NAME,
	adminTool: ADMIN_TOOL_PAGE_NAME,
	fileManager: FILE_MANAGER_PAGE_NAME,
	documentViewer: DOCUMENT_VIEWER_PAGE_NAME,
} = LAUNCH_DARKLY_PAGE_NAMES;

function staticRoute<const T extends RouteObject & { readonly path: NavinaPath }>(route: T): T {
	return route;
}

const NotFound = lazy(() =>
	import('./components/ErrorPage/NotFound/NotFound').then((module) => ({
		default: module.NotFound,
	})),
);

const DocToDocumentRedirect = lazy(() =>
	import('./pages/summary/DocViewer/DocumentViewerFullPage').then((module) => ({
		default: module.DocToDocumentRedirect,
	})),
);

const ExternalRedirectPage = lazy(() =>
	import('./pages/ExternalRedirectPage/ExternalRedirectPage').then((module) => ({
		default: module.ExternalRedirectPage,
	})),
);

const AddTemplate = lazy(() =>
	import('./pages/quality-files-ingestion-tool/AddTemplate').then((module) => ({
		default: module.AddTemplate,
	})),
);

const PatientLookupPage = lazy(() =>
	import('./pages/PatientLookupPage/PatientLookupPage').then((module) => ({
		default: module.PatientLookupPage,
	})),
);

const SummaryNotFoundPage = lazy(() =>
	import('./pages/summary/SummaryNotFoundPage').then((module) => ({
		default: module.SummaryNotFoundPage,
	})),
);

const EpicLogin = lazy(() => import('./pages/epic/login'));

const VeradigmLogin = lazy(() =>
	import('./pages/veradigm/login').then((module) => ({
		default: module.VeradigmLogin,
	})),
);

const Logout = lazy(() => import('./pages/auth/Logout'));

const LoginForm = lazy(() => import('./pages/auth/LoginForm'));

const SsoLoginPage = lazy(() =>
	import('./pages/auth/SsoLogin/SsoLoginPage').then((m) => ({ default: m.SsoLoginPage })),
);

const SsoCallbackPage = lazy(() =>
	import('./pages/auth/SsoLogin/SsoCallbackPage').then((m) => ({ default: m.SsoCallbackPage })),
);

const DocumentViewerFullPage = lazy(() =>
	import('./pages/summary/DocViewer/DocumentViewerFullPage').then((module) => ({
		default: module.DocumentViewerFullPage,
	})),
);

const AnalyticsPage = lazy(() =>
	import('./pages/analytics/AnalyticsPage').then((module) => ({
		default: module.AnalyticsPage,
	})),
);

const AdminToolPage = lazy(() =>
	import('./pages/admin-tool/AdminToolPage').then((module) => ({
		default: module.AdminToolPage,
	})),
);

const MeasuresConfigurationPage = lazy(() =>
	import('./pages/admin-tool/MeasuresConfiguration/MeasuresConfigurationPage').then((module) => ({
		default: module.MeasuresConfigurationPage,
	})),
);

const BulkUserInsertPage = lazy(() =>
	import('./pages/admin-tool/BulkUserInsertPage').then((module) => ({
		default: module.BulkUserInsertPage,
	})),
);

const FileIngestionToolPage = lazy(() =>
	import('./pages/quality-files-ingestion-tool/PageContainer').then((module) => ({
		default: module.FileIngestionToolPage,
	})),
);

const FITTemplatesPage = lazy(() =>
	import('./pages/quality-fit-templates/PageContainer').then((module) => ({
		default: module.FITTemplatesPage,
	})),
);

const QualityTabByNidPage = lazy(() =>
	import('./pages/quality/quality-4-tab-by-nid/PageContainer').then((module) => ({
		default: module.QualityTabByNid,
	})),
);

const AmbientNoteEditorDemoPage = lazy(() =>
	import('./pages/demo/AmbientNoteEditorDemo').then((module) => ({
		default: module.AmbientNoteEditorDemo,
	})),
);

const MobileLoginQRPage = lazy(() =>
	import('./pages/ambient/mobile-qr-page/MobileQRPage').then((module) => ({
		default: module.MobileQRPage,
	})),
);

const SchedulePage = lazy(() =>
	import('./pages/SchedulePage/SchedulePage').then((module) => ({
		default: module.SchedulePage,
	})),
);

const Quality = lazy(() =>
	import('./pages/quality/care-team/PageContainer').then((module) => ({
		default: module.PageContainer,
	})),
);

const PatientLookupHeader = lazy(() =>
	import('./pages/PatientLookupPage/PatientLookupHeader').then((module) => ({
		default: module.PatientLookupHeader,
	})),
);

const FileManager = lazy(() =>
	import('./pages/FileManager/FileManagerPage').then((module) => ({
		default: module.FileManagerPage,
	})),
);

const ReviewOptimizationsPage = lazy(() =>
	import('./pages/ReviewOptimizationsPage/ReviewOptimizationsPage').then((module) => ({
		default: module.ReviewOptimizationsPage,
	})),
);

const CareTeamHeader = lazy(() =>
	import('./pages/quality/care-team/CareTeamHeader').then((module) => ({
		default: module.CareTeamHeader,
	})),
);

const AUTHORIZED_USER_ACTION_ROUTES = [
	staticRoute({
		path: PATHS.AUTH_CHANGE_PASSWORD_PATH,
		element: (
			<LoginForm
				{...{
					defaultStepId: NavinaLoginStep.ChangePassword,
				}}
			/>
		),
	}),
	{
		...withLayout(<SpinnerLayout />, [
			staticRoute({
				path: PATHS.LOGOUT_PATH,
				element: <Logout />,
			}),
		]),
	},
] as const satisfies ReadonlyArray<RouteObject>;

function getChromeExtensionStoreUrl(chromePublicExtensionId: string): string {
	return `https://chromewebstore.google.com/detail/navina-addon/${chromePublicExtensionId}`;
}

interface ChromeExtensionRoute {
	readonly path: NavinaPath;
	readonly chromePublicExtensionId: string;
}

const CHROME_EXTENSIONS = [
	{
		path: PATHS.ATHENA_PATH,
		chromePublicExtensionId: 'plhceajggdihcnffppedpfjpflpmdakh',
	},
	{
		path: PATHS.ECW_PATH,
		chromePublicExtensionId: 'clkcfpclllbaegehjacadbpejiocgkii',
	},
] as const satisfies ReadonlyArray<ChromeExtensionRoute>;

const CHROME_EXTENSION_STORE_REDIRECTS = CHROME_EXTENSIONS.map(({ path, chromePublicExtensionId }) =>
	staticRoute({
		path,
		element: (
			<ExternalRedirectPage
				{...{
					url: getChromeExtensionStoreUrl(chromePublicExtensionId),
				}}
			/>
		),
	}),
);

const SCHEDULE_PAGE_ROUTES = [
	{
		...withLaunchDarklyPage(SCHEDULING_OVERVIEW_PAGE_NAME),
		...withLayout(
			<MainLayout
				{...({
					headerAppMenuSelectedPage: ApplicationsMenuSelectedPage.PatientPortrait,
					sideHeaderContent: <PatientLookupHeader />,
					headerTitle: 'Scheduling Overview',
					headerLogoLink: LogoDestination.Schedule,
				} as const satisfies LayoutHeaderProps)}
			/>,
			[
				staticRoute({
					path: PATHS.ROOT_PATH,
					element: <Navigate to={PATHS.SCHEDULE_PATH} />,
				}),
				staticRoute({
					path: PATHS.SCHEDULE_PATH,
					element: <SchedulePage />,
				}),
			],
		),
	},
] as const satisfies ReadonlyArray<RouteObject>;

const PATIENT_LOOKUP_ROUTES = [
	{
		...withLaunchDarklyPage(PATIENT_LOOKUP_PAGE_NAME),
		...withLayout(
			<MainLayout
				{...({
					headerAppMenuSelectedPage: ApplicationsMenuSelectedPage.PatientPortrait,
					sideHeaderContent: <PatientLookupHeader />,
					headerTitle: 'Patient Lookup',
				} as const satisfies LayoutHeaderProps)}
			/>,
			[
				{
					path: '/patient-lookup/:navinaPatientId',
					element: <PatientLookupPage />,
				},
			],
		),
	},
] as const satisfies ReadonlyArray<RouteObject>;

const ANALYTICS_ROUTES = [
	{
		...withLaunchDarklyPage(ANALYTICS_DASHBOARD_PAGE_NAME),
		...withLayout(
			<MainLayout
				{...({
					headerTitle: 'Analytics',
					headerAppMenuSelectedPage: ApplicationsMenuSelectedPage.Dashboard,
				} as const satisfies LayoutHeaderProps)}
			/>,
			[
				{
					path: `${PATHS.ANALYTICS_PATH}/*`,
					element: <AnalyticsPage />,
				},
				staticRoute({
					path: PATHS.ANALYTICS_QUICKSIGHT_PATH,
					element: <Navigate to={PATHS.ANALYTICS_PATH} />,
				}),
			],
		),
	},
] as const satisfies ReadonlyArray<RouteObject>;

const ADMIN_ROUTES = [
	{
		...withLaunchDarklyPage(ADMIN_TOOL_PAGE_NAME),
		...withLayout(
			<MainLayout
				{...({
					headerTitle: 'Admin',
					headerAppMenuSelectedPage: ApplicationsMenuSelectedPage.Admin,
				} as const satisfies LayoutHeaderProps)}
			/>,
			[
				withLayout(<AdminLayout />, [
					{
						path: PATHS.ADMIN_PATH,
						element: (
							<ResponsivePaddingLayoutContainer>
								<AdminToolPage />
							</ResponsivePaddingLayoutContainer>
						),
					},
					{
						path: PATHS.ADMIN_MEASURES_CONFIG_PATH,
						element: <MeasuresConfigurationPage />,
					},
					{
						path: PATHS.ADMIN_INSERT_USERS_PATH,
						element: (
							<ResponsivePaddingLayoutContainer>
								<BulkUserInsertPage />
							</ResponsivePaddingLayoutContainer>
						),
					},
				]),
			],
		),
	},
] as const satisfies ReadonlyArray<RouteObject>;

const QUALITY_WORKSPACE_ROUTE_PROPS = {
	sideHeaderContent: <CareTeamHeader />,
	headerAppMenuSelectedPage: ApplicationsMenuSelectedPage.Quality,
	headerLogoLink: LogoDestination.Nowhere,
	headerTitle: 'Quality Workspace',
} as const satisfies LayoutHeaderProps;

const QUALITY_POP_WORKSPACE_ROUTE_PROPS = {
	...QUALITY_WORKSPACE_ROUTE_PROPS,
	headerTitle: 'Pop Health Workspace',
} as const satisfies LayoutHeaderProps;

const QualityWorkspaceHeaderTitle = observer(function QualityWorkspaceHeaderTitle() {
	const popHealthEnabled = useFeatureFlag('navina_user_features', false);

	return popHealthEnabled ? QUALITY_POP_WORKSPACE_ROUTE_PROPS.headerTitle : QUALITY_WORKSPACE_ROUTE_PROPS.headerTitle;
});

const QUALITY_WORKSPACE_DYNAMIC_ROUTE_PROPS = {
	...QUALITY_WORKSPACE_ROUTE_PROPS,
	headerTitle: <QualityWorkspaceHeaderTitle />,
} as const satisfies LayoutHeaderProps;

const QUALITY_WORKSPACE_ROUTES = [
	{
		...withLayout(<MainLayout {...QUALITY_WORKSPACE_ROUTE_PROPS} />, [
			{
				element: <Quality />,
				children: [
					{
						path: PATHS.QUALITY_SATISFACTION_RATE_PATH,
					},
				],
			},
		]),
	},
] as const satisfies ReadonlyArray<RouteObject>;

const FILE_MANAGER_ROUTES = [
	{
		...withLaunchDarklyPage(FILE_MANAGER_PAGE_NAME),
		...withLayout(
			<MainLayout
				{...({
					headerAppMenuSelectedPage: ApplicationsMenuSelectedPage.Files,
					headerTitle: 'File Manager',
					headerLogoLink: LogoDestination.Nowhere,
				} as const satisfies LayoutHeaderProps)}
			/>,
			[
				{
					element: <FileManager />,
					path: PATHS.FILES_PATH,
				},
			],
		),
	},
] as const satisfies ReadonlyArray<RouteObject>;

/**
 * Transitional redirect. The file manager lived at `/v2/files` during the
 * unified-manager rollout; now that it owns `/files`, this forwards the old URL
 * so existing bookmarks and links keep working. Remove once `/v2/files` traffic
 * has dropped off.
 */
const FILES_V2_REDIRECT_ROUTES = [
	staticRoute({
		path: PATHS.FILES_UNIFIED_PATH,
		element: <Navigate to={PATHS.FILES_PATH} replace />,
	}),
] as const satisfies ReadonlyArray<RouteObject>;

const QUALITY_FIT_ROUTES = [
	{
		...withLayout(
			<MainLayout
				{...({
					headerAppMenuSelectedPage: ApplicationsMenuSelectedPage.Quality,
					headerLogoLink: LogoDestination.Nowhere,
				} as const satisfies LayoutHeaderProps)}
			/>,
			[
				{
					path: PATHS.QUALITY_FIT_PATH,
					element: <FileIngestionToolPage />,
				},
				{
					path: PATHS.QUALITY_FIT_ADD_TEMPLATE_PATH,
					element: <AddTemplate />,
				},
				{
					path: PATHS.QUALITY_FIT_TEMPLATES_PATH,
					element: <FITTemplatesPage />,
				},
			],
		),
	},
] as const satisfies ReadonlyArray<RouteObject>;

const QUALITY_PATIENT_BASED_ROUTES = [
	{
		...withLayout(
			<MainLayout
				{...({
					...QUALITY_WORKSPACE_ROUTE_PROPS,
					headerTitle: 'Quality patient view',
				} as const satisfies LayoutHeaderProps)}
			/>,
			[
				{
					path: PATHS.QUALITY_TAB_PATH,
					element: <QualityTabByNidPage />,
				},
			],
		),
	},
] as const satisfies ReadonlyArray<RouteObject>;

const QUALITY_POP_WORKSPACE_ROUTES = [
	{
		...withLayout(<MainLayout {...QUALITY_POP_WORKSPACE_ROUTE_PROPS} />, [
			{
				element: <Quality />,
				children: [
					{
						path: PATHS.POP_HEALTH_ADMISSIONS_PATH,
					},
					{
						path: PATHS.POP_HEALTH_RADV_AUDIT_PATH,
					},
					{
						path: PATHS.POP_HEALTH_COST_PATH,
					},
				],
			},
		]),
	},
] as const satisfies ReadonlyArray<RouteObject>;

const SUMMARY_LANDING_PAGE_PROPS = {
	overlayPageName: null,
} as const satisfies ComponentProps<typeof SummaryLandingPage>;

const SUMMARY_ROUTES = [
	{
		...withLaunchDarklyPage(APPOINTMENT_BASED_SUMMARY_PAGE_NAME),
		element: <SummaryNotFoundPage hideActionButton={false} />,
		children: [
			staticRoute({
				path: PATHS.SUMMARY_GUESS_MATCH_NOT_FOUND_PATH,
			}),
			{
				path: `${EMBEDDED_LEGACY_APPOINTMENT_PORTRAIT_PATH_PREFIX}${PATHS.SUMMARY_GUESS_MATCH_NOT_FOUND_PATH}`,
			},
			{
				path: `${EMBEDDED_PATIENT_PORTRAIT_PATH_PREFIX}${PATHS.SUMMARY_GUESS_MATCH_NOT_FOUND_PATH}`,
			},
		],
	},
	{
		...withLaunchDarklyPage(APPOINTMENT_BASED_SUMMARY_PAGE_NAME),
		path: '/:navinaAppointmentId',
		element: <SummaryLandingPage {...SUMMARY_LANDING_PAGE_PROPS} />,
	},
	{
		...withLaunchDarklyPage(APPOINTMENT_BASED_SUMMARY_PAGE_NAME),
		path: `${EMBEDDED_LEGACY_APPOINTMENT_PORTRAIT_PATH_PREFIX}/:navinaAppointmentId`,
		element: <SummaryLandingPage {...SUMMARY_LANDING_PAGE_PROPS} />,
	},
	{
		...withLaunchDarklyPage(APPOINTMENT_BASED_SUMMARY_PAGE_NAME),
		path: `${EMBEDDED_LEGACY_APPOINTMENT_PORTRAIT_PATH_PREFIX}/hcc/:navinaAppointmentId`,
		element: (
			<SummaryLandingPage
				{...{
					...SUMMARY_LANDING_PAGE_PROPS,
					overlayPageName: 'hcc',
				}}
			/>
		),
	},
	{
		...withLaunchDarklyPage(APPOINTMENT_BASED_SUMMARY_PAGE_NAME),
		path: `${EMBEDDED_LEGACY_APPOINTMENT_PORTRAIT_PATH_PREFIX}/clinical/:navinaAppointmentId`,
		element: (
			<SummaryLandingPage
				{...{
					...SUMMARY_LANDING_PAGE_PROPS,
					overlayPageName: 'portrait',
				}}
			/>
		),
	},
	{
		...withLaunchDarklyPage(APPOINTMENT_BASED_SUMMARY_PAGE_NAME),
		path: `${EMBEDDED_LEGACY_APPOINTMENT_PORTRAIT_PATH_PREFIX}/portrait/:navinaAppointmentId`,
		element: (
			<SummaryLandingPage
				{...{
					...SUMMARY_LANDING_PAGE_PROPS,
					overlayPageName: 'portrait',
				}}
			/>
		),
	},
	{
		...withLaunchDarklyPage(APPOINTMENT_BASED_SUMMARY_PAGE_NAME),
		path: `${EMBEDDED_LEGACY_APPOINTMENT_PORTRAIT_PATH_PREFIX}/documents/:navinaAppointmentId`,
		element: (
			<SummaryLandingPage
				{...{
					...SUMMARY_LANDING_PAGE_PROPS,
					overlayPageName: 'documents',
				}}
			/>
		),
	},
	{
		...withLaunchDarklyPage(APPOINTMENT_BASED_SUMMARY_PAGE_NAME),
		path: `${EMBEDDED_LEGACY_APPOINTMENT_PORTRAIT_PATH_PREFIX}/alerts/:navinaAppointmentId`,
		element: (
			<SummaryLandingPage
				{...{
					...SUMMARY_LANDING_PAGE_PROPS,
					overlayPageName: 'alerts',
				}}
			/>
		),
	},
	{
		...withLaunchDarklyPage(APPOINTMENT_BASED_SUMMARY_PAGE_NAME),
		path: `${EMBEDDED_LEGACY_APPOINTMENT_PORTRAIT_PATH_PREFIX}/ambient/:navinaAppointmentId`,
		element: (
			<SummaryLandingPage
				{...{
					...SUMMARY_LANDING_PAGE_PROPS,
					overlayPageName: 'ambient',
				}}
			/>
		),
	},
	{
		...withLaunchDarklyPage(APPOINTMENT_BASED_SUMMARY_PAGE_NAME),
		path: `${EMBEDDED_PATIENT_PORTRAIT_PATH_PREFIX}/:navinaAppointmentId`,
		element: <SummaryLandingPage {...SUMMARY_LANDING_PAGE_PROPS} />,
	},
	{
		...withLaunchDarklyPage(APPOINTMENT_BASED_SUMMARY_PAGE_NAME),
		path: `${EMBEDDED_PATIENT_PORTRAIT_PATH_PREFIX}/hcc/:navinaAppointmentId`,
		element: (
			<SummaryLandingPage
				{...{
					...SUMMARY_LANDING_PAGE_PROPS,
					overlayPageName: 'hcc',
				}}
			/>
		),
	},
	{
		...withLaunchDarklyPage(APPOINTMENT_BASED_SUMMARY_PAGE_NAME),
		path: `${EMBEDDED_PATIENT_PORTRAIT_PATH_PREFIX}/clinical/:navinaAppointmentId`,
		element: (
			<SummaryLandingPage
				{...{
					...SUMMARY_LANDING_PAGE_PROPS,
					overlayPageName: 'portrait',
				}}
			/>
		),
	},
	{
		...withLaunchDarklyPage(APPOINTMENT_BASED_SUMMARY_PAGE_NAME),
		path: `${EMBEDDED_PATIENT_PORTRAIT_PATH_PREFIX}/portrait/:navinaAppointmentId`,
		element: (
			<SummaryLandingPage
				{...{
					...SUMMARY_LANDING_PAGE_PROPS,
					overlayPageName: 'portrait',
				}}
			/>
		),
	},
	{
		...withLaunchDarklyPage(APPOINTMENT_BASED_SUMMARY_PAGE_NAME),
		path: `${EMBEDDED_PATIENT_PORTRAIT_PATH_PREFIX}/documents/:navinaAppointmentId`,
		element: (
			<SummaryLandingPage
				{...{
					...SUMMARY_LANDING_PAGE_PROPS,
					overlayPageName: 'documents',
				}}
			/>
		),
	},
	{
		...withLaunchDarklyPage(APPOINTMENT_BASED_SUMMARY_PAGE_NAME),
		path: `${EMBEDDED_PATIENT_PORTRAIT_PATH_PREFIX}/alerts/:navinaAppointmentId`,
		element: (
			<SummaryLandingPage
				{...{
					...SUMMARY_LANDING_PAGE_PROPS,
					overlayPageName: 'alerts',
				}}
			/>
		),
	},
	{
		...withLaunchDarklyPage(APPOINTMENT_BASED_SUMMARY_PAGE_NAME),
		path: `${EMBEDDED_PATIENT_PORTRAIT_PATH_PREFIX}/ambient/:navinaAppointmentId`,
		element: (
			<SummaryLandingPage
				{...{
					...SUMMARY_LANDING_PAGE_PROPS,
					overlayPageName: 'ambient',
				}}
			/>
		),
	},
] as const satisfies ReadonlyArray<RouteObject>;

const DOCUMENT_VIEWER_ROUTES = [
	{
		...withLaunchDarklyPage(DOCUMENT_VIEWER_PAGE_NAME),
		path: '/doc/:documentId',
		element: <DocToDocumentRedirect />,
	},
	{
		...withLaunchDarklyPage(DOCUMENT_VIEWER_PAGE_NAME),
		path: '/document/:documentId',
		element: <DocumentViewerFullPage />,
	},
] as const satisfies ReadonlyArray<RouteObject>;

const AMBIENT_ROUTES = [
	staticRoute({
		path: PATHS.AMBIENT_NOTE_DEMO_PATH,
		element: <AmbientNoteEditorDemoPage />,
	}),
	staticRoute({
		path: PATHS.MOBILE_LOGIN_QR_PATH,
		element: <MobileLoginQRPage />,
	}),
] as const satisfies ReadonlyArray<RouteObject>;

const PAGE_NOT_FOUND_ROUTE = {
	path: '*',
	element: (
		<NotFound
			{...{
				entityName: 'Page',
				action: {
					onAction: function goHome(): void {
						globalThis.location.href = '/';
					},
					name: 'Go to Scheduling Overview',
				},
			}}
		/>
	),
} as const satisfies RouteObject;

const QUALITY_WORKSPACE_DYNAMIC_ROUTES = [
	{
		...withLayout(<MainLayout {...QUALITY_WORKSPACE_DYNAMIC_ROUTE_PROPS} />, [
			{
				element: <Quality />,
				children: [
					{
						path: PATHS.QUALITY_PATH,
					},
					{
						path: PATHS.QUALITY_WORKSPACE_PATH,
					},
					{
						path: `${EMBEDDED_PATIENT_PORTRAIT_PATH_PREFIX}/${PATH_NAMES.QUALITY_WORKSPACE}`,
					},
					{
						path: `${EMBEDDED_LEGACY_APPOINTMENT_PORTRAIT_PATH_PREFIX}/${PATH_NAMES.QUALITY_WORKSPACE}`,
					},
					{
						path: PATHS.QUALITY_ARCHIVE_PATH,
					},
					{
						path: PATHS.QUALITY_PATIENTS_PATH,
					},
				],
			},
		]),
	},
] as const satisfies ReadonlyArray<RouteObject>;

const GUEST_ONLY_AND_ANYONE_ROUTES = [
	{
		element: <GuestOnlyRoute />,
		children: [
			{
				...withLayout(<AuthLayout />, [
					{
						element: <LoginForm />,
						children: [
							staticRoute({
								path: PATHS.SSO_PATH,
							}),
							{
								path: `${PATHS.SSO_PATH}/*`,
							},
						],
					},
					{
						element: <SsoLoginPage />,
						children: [
							staticRoute({
								path: PATHS.SSO_LOGIN_PATH,
							}),
						],
					},
				]),
			},
		],
	},
	{
		// AnyoneRoute
		children: [
			{
				...withLayout(<SpinnerLayout />, [
					{
						element: <SsoCallbackPage />,
						children: [
							staticRoute({
								path: PATHS.SSO_CALLBACK_PATH,
							}),
						],
					},
				]),
			},
			{
				...withLayout(<SpinnerLayout keepSpinnerVisible />, [
					{
						element: <EpicLogin />,
						children: [
							{
								path: `/epic/${PATH_NAMES.LOGIN}`,
							},
							{
								path: `/epic/${PATH_NAMES.LOGIN}/*`,
							},
							{
								path: `${EMBEDDED_LEGACY_APPOINTMENT_PORTRAIT_PATH_PREFIX}/epic/${PATH_NAMES.LOGIN}`,
							},
							{
								path: `${EMBEDDED_LEGACY_APPOINTMENT_PORTRAIT_PATH_PREFIX}/epic/${PATH_NAMES.LOGIN}/*`,
							},

							{
								path: `${EMBEDDED_PATIENT_PORTRAIT_PATH_PREFIX}/epic/${PATH_NAMES.LOGIN}`,
							},
							{
								path: `${EMBEDDED_PATIENT_PORTRAIT_PATH_PREFIX}/epic/${PATH_NAMES.LOGIN}/*`,
							},
						],
					},
				]),
			},
			{
				...withLayout(<SpinnerLayout />, [
					staticRoute({
						element: <VeradigmLogin />,
						path: PATHS.VERADIGM_LAUNCH_PATH,
					}),
					{
						element: <VeradigmLogin />,
						path: `${PATHS.VERADIGM_LAUNCH_PATH}/*`,
					},
				]),
			},
		],
	},
	{
		element: <GuestOnlyRoute />,
		children: [
			{
				...withLayout(<SpinnerLayout />, [
					{
						element: <LoginForm />,
						children: [
							{
								path: PATHS.LOGIN_PATH,
							},
							{
								path: `${PATHS.LOGIN_PATH}/*`,
							},
							{
								path: `${EMBEDDED_LEGACY_APPOINTMENT_PORTRAIT_PATH_PREFIX}/${PATH_NAMES.LOGIN}`,
							},
							{
								path: `${EMBEDDED_LEGACY_APPOINTMENT_PORTRAIT_PATH_PREFIX}/${PATH_NAMES.LOGIN}/*`,
							},
							{
								path: `${EMBEDDED_PATIENT_PORTRAIT_PATH_PREFIX}/${PATH_NAMES.LOGIN}`,
							},
							{
								path: `${EMBEDDED_PATIENT_PORTRAIT_PATH_PREFIX}/${PATH_NAMES.LOGIN}/*`,
							},
						],
					},
				]),
			},
		],
	},
] as const satisfies ReadonlyArray<RouteObject>;

const REVIEW_OPTIMIZATIONS_ROUTES = [
	{
		path: `${PATHS.REVIEW_OPTIMIZATIONS_PATH}/:jobId`,
		element: <ReviewOptimizationsPage />,
	},
] as const satisfies ReadonlyArray<RouteObject>;

const PROTECTED_ROUTES = [
	{
		element: <ProtectedRoute />,
		children: [
			...AUTHORIZED_USER_ACTION_ROUTES,
			...SCHEDULE_PAGE_ROUTES,
			...PATIENT_LOOKUP_ROUTES,
			...PATIENT_PORTRAIT_ROUTES,
			...ANALYTICS_ROUTES,
			...ADMIN_ROUTES,
			...QUALITY_WORKSPACE_DYNAMIC_ROUTES,
			...QUALITY_WORKSPACE_ROUTES,
			...FILE_MANAGER_ROUTES,
			...FILES_V2_REDIRECT_ROUTES,
			...QUALITY_FIT_ROUTES,
			...QUALITY_PATIENT_BASED_ROUTES,
			...QUALITY_POP_WORKSPACE_ROUTES,
			...POPULATION_HEALTH_MANAGEMENT_ROUTES,
			...DOCUMENT_VIEWER_ROUTES,
			...AMBIENT_ROUTES,
			...REVIEW_OPTIMIZATIONS_ROUTES,
		],
	},
] as const satisfies ReadonlyArray<RouteObject>;

// This could be removed once ProtectedRoute's `isSummaryView` prop is gone
const SUMMARY_PROTECTED_ROUTES = [
	{
		element: <ProtectedRoute isSummaryView={true} />,
		children: [...SUMMARY_ROUTES],
	},
] as const satisfies ReadonlyArray<RouteObject>;

const ANYONE_ROUTES = [
	{
		children: [...CHROME_EXTENSION_STORE_REDIRECTS],
	},
] as const satisfies ReadonlyArray<RouteObject>;

const APP_ROUTES = [
	{
		element: <RootLayout />,
		errorElement: <ErrorBoundary />,
		children: [
			...GUEST_ONLY_AND_ANYONE_ROUTES,
			...ANYONE_ROUTES,
			...PROTECTED_ROUTES,
			...SUMMARY_PROTECTED_ROUTES,
			PAGE_NOT_FOUND_ROUTE,
		],
	},
] as const satisfies ReadonlyArray<RouteObject>;

const APP_ROUTER = createBrowserRouter([...APP_ROUTES]);

// Patient portraits report their own lifecycle straight off the router, so features can be told that
// a portrait opened or closed without a layout having to know which features care - and without
// anything running while the user is in a product that has nothing to do with portraits.
tracePatientPortraitsOnRouter({
	router: APP_ROUTER,
	patientPortraitLifecycleModel: getOrCreatePatientPortraitLifecycleModel(),
});

export function App() {
	slownessMeasure.timeLog('React has started');

	useEffect(function applyFaviconColorOverride(): void {
		if (FAVICON_COLOR_OVERRIDE) {
			setFaviconColor({ colorOverride: FAVICON_COLOR_OVERRIDE });
		}
	}, []);

	return (
		<RouterProvider
			{...{
				router: APP_ROUTER,
			}}
		/>
	);
}
